import React, { useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import { cn } from "../../lib/utils";
import { useMmText } from "../../hooks/useMmText";
import type { MmOperatorMastery, MmRegister } from "../../api/mentalModels";

/**
 * ICDF §4 — "Your moves": the operator families as a growing skill
 * constellation. Identity language, not gamification: dimmed = not yet
 * walked, glowing = fluent. Derived entirely from the user's real
 * operator applications (mm_events) — nothing to earn, only to notice.
 */

const FAMILY_ORDER = ["create", "form", "relations", "epistemic", "affect", "design"];

const FAMILY_DOTS: Record<string, string> = {
  create: "bg-sky-400",
  form: "bg-amber-400",
  relations: "bg-violet-400",
  epistemic: "bg-emerald-400",
  affect: "bg-rose-400",
  design: "bg-indigo-400",
};

const FAMILY_FALLBACKS: Record<string, string> = {
  create: "Meaning & boundaries",
  form: "Shape & size",
  relations: "Relations",
  epistemic: "Knowledge & certainty",
  affect: "Feeling & stance",
  design: "Design & build",
};

const LEVEL_CHIP: Record<string, string> = {
  new: "border-dashed border-border text-muted-foreground/50",
  tried: "border-border text-muted-foreground",
  practicing: "border-primary/40 bg-primary/5 text-primary/90",
  fluent: "border-primary bg-primary/10 text-primary font-medium shadow-[0_0_10px_-2px] shadow-primary/40",
};

interface Props {
  mastery: MmOperatorMastery[] | null;
  reasonMix: { friction: number; plan: number };
  register: MmRegister;
}

const MmMasteryPanel: React.FC<Props> = ({ mastery, reasonMix, register }) => {
  const { tm } = useMmText(register);
  const [open, setOpen] = useState(false);

  if (!mastery || mastery.length === 0) return null;

  const triedCount = mastery.filter((m) => m.level !== "new").length;
  const totalReasons = reasonMix.friction + reasonMix.plan;

  return (
    <div className="mt-6 rounded-2xl border border-border/70 bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-start"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="w-4 h-4 text-primary/60" />
          {tm("mastery_title", "Your moves")}
          <span className="text-xs font-normal text-muted-foreground">
            {tm("mastery_count", "{{tried}} of {{total}} explored")
              .replace("{{tried}}", String(triedCount))
              .replace("{{total}}", String(mastery.length))}
          </span>
        </span>
        <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {FAMILY_ORDER.map((family) => {
            const ops = mastery.filter((m) => m.family === family);
            if (!ops.length) return null;
            return (
              <div key={family}>
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
                  <span className={cn("h-1.5 w-1.5 rounded-full", FAMILY_DOTS[family])} />
                  {tm(`family_${family}`, FAMILY_FALLBACKS[family] ?? family)}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {ops.map((m) => (
                    <span
                      key={m.operatorId}
                      title={
                        m.uses > 0
                          ? tm("mastery_uses", "Used {{count}} times").replace(
                              "{{count}}",
                              String(m.uses)
                            )
                          : tm("mastery_never", "Not yet tried")
                      }
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                        LEVEL_CHIP[m.level]
                      )}
                    >
                      {tm(`op_${m.operatorId}_title`, m.operatorId)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
          {totalReasons > 0 && (
            <p className="pt-2 border-t border-border/50 text-[11px] text-muted-foreground">
              {tm("mastery_reason_mix", "How you arrive: {{friction}} from friction · {{plan}} from a plan")
                .replace("{{friction}}", String(reasonMix.friction))
                .replace("{{plan}}", String(reasonMix.plan))}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MmMasteryPanel;
